import {
  ActionLink,
  B2BSpinner,
  ButtonWrap,
  CloseButtonIcon,
  CorporateTypography,
  FlightCategoryRadio,
  Header,
  Icon,
  IconName,
  LoadingPopup,
  usePrevious,
} from "halifax";
import React, { useEffect } from "react";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { RouteComponentProps } from "react-router";
import {
  CallState,
  FareclassOptionFilter,
  IResponse,
  ITripTerminus,
  LocationQueryEnum,
  PackagesEntryTypeEnum,
  RecentFlightSearch,
  TripCategory,
} from "redmond";
import {
  getConfigTenant,
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useIsTravelApp,
} from "@capone/common";
import {
  getExperimentVariantCustomVariants,
  NOT_SELF_FUNDED,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  SELF_FUNDED,
  useExperiments,
} from "../../../../context/experiments";
import { generatePackagesCTAUrl } from "../../../../utils/urlHelpers";
import { PATH_HOME } from "../../../../utils/urlPaths";
import { MulticityFlightShopStep } from "../../../shop/reducer";
import { MobileFlightSearchStep } from "../../reducer";
import { LocationSearch, MobileCalendarPicker } from "./components";
import { MobileFlightSearchControlV3ConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";
import {
  IFlightParsedQuery,
  parseQueryString,
} from "../../utils/parseQueryString";
import { fetchLocationAutocomplete } from "../../../../api/v0/search/fetchLocationAutocomplete";
import { ITripTerminusCategory } from "../../types";

export interface IMobileFlightSearchControlV3Props
  extends MobileFlightSearchControlV3ConnectorProps,
    RouteComponentProps {
  isMultiCityEnabled?: boolean;
  recentSearches?: RecentFlightSearch[];
  onRecentSearchClick?: (search: RecentFlightSearch) => void;
  onSelectLocation?: (value: any) => void;
  onSelectDates?: (departure: Date | null, returnDate: Date | null) => void;
  isAirCXV3Experiment?: boolean;
  isCustomerProfileExperiment?: boolean;
  fareclassOptionFilter: FareclassOptionFilter;
}

export const MobileFlightSearchControlV3 = (
  props: IMobileFlightSearchControlV3Props
) => {
  const {
    origin,
    destination,
    originCode,
    destinationCode,
    tripCategory,
    fetchDepartureCalendar,
    setCalendar,
    departureDateBucketsCallState,
    currentStep,
    departureDate,
    returnDate,
    setCurrentStep,
    setTripCategory,
    history,
    hasOriginAutocompleteError,
    hasDestinationAutocompleteError,
    setOriginAutocompleteError,
    setDestinationAutocompleteError,
    isMultiCityEnabled,
    populateFlightShopQueryParams,
    recentSearches,
    onRecentSearchClick,
    onSelectLocation,
    onSelectDates,
    isAirCXV3Experiment,
    fareclassOptionFilter,
    stopsOption,
    adultsCount,
    childrenCount,
    infantsOnLapCount,
    infantsInSeatCount,
    setOrigin,
    setDestination,
    setPassengerCounts,
    setStopsOption,
  } = props;

  const expState = useExperiments();
  const isCorporate = isCorpTenant(getConfigTenant());

  const focusedMonthIndex = departureDate
    ? dayjs(departureDate).diff(dayjs(), "month")
    : 0;

  const parsedQueryString = parseQueryString(history);
  const { isTravelApp } = useIsTravelApp();
  const [isInitializing, setIsInitializing] = React.useState(true);

  const populateFieldsFromQueryString = async ({
    adultsCount: queryParamAdultsCount,
    childrenCount: queryParamChildrenCount,
    infantsInSeatCount: queryParamInfantsInSeatCount,
    infantsOnLapCount: queryParamsInfantsOnLapCount,
    origin: queryParamOrigin,
    originRegionType,
    destination: queryParamDestination,
    destinationRegionType,
    tripCategory: queryParamTripCategory,
    stopsOption: queryParamStopsOption,
  }: IFlightParsedQuery) => {
    if (queryParamOrigin) {
      const { categories: originCategories }: IResponse =
        await fetchLocationAutocomplete({
          LocationQuery: LocationQueryEnum.Label,
          l: queryParamOrigin,
        });
      const correspondingOrigin = originCategories
        .flatMap((category) => (category as ITripTerminusCategory).results)
        .find(
          (result) =>
            result.id.code.code === queryParamOrigin &&
            (originRegionType
              ? result.id.code.regionType === originRegionType
              : true)
        ) as ITripTerminus;
      setOrigin(correspondingOrigin);
      if (correspondingOrigin) {
        if (hasOriginAutocompleteError) {
          setOriginAutocompleteError(false);
        }
      } else {
        setOriginAutocompleteError(true);
      }
    }
    if (queryParamDestination) {
      const { categories: destinationCategories }: IResponse =
        await fetchLocationAutocomplete({
          LocationQuery: LocationQueryEnum.Label,
          l: queryParamDestination,
        });
      const correspondingDestination = destinationCategories
        .flatMap((category) => (category as ITripTerminusCategory).results)
        .find(
          (result) =>
            result.id.code.code === queryParamDestination &&
            (destinationRegionType
              ? result.id.code.regionType === destinationRegionType
              : true)
        ) as ITripTerminus;
      setDestination(correspondingDestination);
    }

    if (
      queryParamAdultsCount ||
      queryParamChildrenCount ||
      queryParamInfantsInSeatCount ||
      queryParamsInfantsOnLapCount
    ) {
      setPassengerCounts({
        adultsCount: queryParamAdultsCount ?? 1,
        childrenCount: queryParamChildrenCount ?? 0,
        infantsInSeatCount: queryParamInfantsInSeatCount ?? 0,
        infantsOnLapCount: queryParamsInfantsOnLapCount ?? 0,
      });
    }
    if (queryParamTripCategory) {
      setTripCategory(queryParamTripCategory);
    }
    if (queryParamStopsOption) {
      setStopsOption(queryParamStopsOption);
    }
  };

  React.useEffect(() => {
    (async () => {
      if (parsedQueryString) {
        await populateFieldsFromQueryString(parsedQueryString);
      }
    })();
  }, []);

  React.useEffect(() => {
    if (isTravelApp) {
      const hasQueryParams =
        parsedQueryString &&
        (parsedQueryString.origin || parsedQueryString.destination);

      // If we have query params, wait for origin AND destination to be loaded into Redux
      // If no query params, we can proceed immediately
      const hasLoadedQueryParams = hasQueryParams
        ? origin && destination
        : true;

      // Also wait for calendar data to finish loading if we have origin/destination
      const isCalendarLoading =
        origin &&
        destination &&
        departureDateBucketsCallState === CallState.InProcess;
      const canShowCalendar = hasLoadedQueryParams && !isCalendarLoading;

      if (canShowCalendar) {
        // Set step to CalendarPicker if not already there
        if (currentStep !== MobileFlightSearchStep.CalendarPicker) {
          setCurrentStep(MobileFlightSearchStep.CalendarPicker);
          // Don't mark as initialized yet - wait for currentStep to actually update
        } else {
          // Step is already CalendarPicker, mark as initialized
          setIsInitializing(false);
        }
      }
    } else {
      // Not a travel app, no special initialization needed
      setIsInitializing(false);
    }
  }, [
    isTravelApp,
    origin,
    destination,
    currentStep,
    departureDateBucketsCallState,
  ]);

  const handleGoBack = () => {
    setCurrentStep(
      currentStep > MobileFlightSearchStep.LocationSearch
        ? currentStep - 1
        : currentStep
    );
  };

  const getCorporateInfoHeader = () => {
    return textConstants.CORPORATE_HOTELS_AND_FLIGHTS_INFO_NEW_PACKAGES_CTA_HEADER;
  };

  const renderPackagesCTA = () => {
    const handlePackagesCTAClick = () => {
      const url = generatePackagesCTAUrl({
        origin,
        destination,
        fromDate: departureDate,
        untilDate: returnDate,
        stopsOption,
        fareClass: fareclassOptionFilter,
        adultsCount,
        childrenCount,
        infantsInSeatCount,
        infantsOnLapCount,
        matchesMobile: true,
        entryPoint: PackagesEntryTypeEnum.FLIGHT_SEARCH,
      });

      history.push(url);
    };

    return (
      <ButtonWrap
        className="flights-info-cta"
        onClick={() => {
          handlePackagesCTAClick();
        }}
      >
        <div className="header-container">
          <div className="packages-icon-wrapper">
            <Icon name={IconName.HotelFunnelIcon} />
            <Typography className="icon-plus-separator">+</Typography>
            <Icon name={IconName.FlightFunnelIcon} />
          </div>
          {isCorporate ? (
            <CorporateTypography variant="body1">
              {getCorporateInfoHeader()}
            </CorporateTypography>
          ) : (
            <Typography>
              {textConstants.INFO_NEW_PACKAGES_CTA_HEADER}
            </Typography>
          )}
          <Box
            className={clsx("new-tag", {
              corporate: isCorporate,
            })}
          >
            New
          </Box>
        </div>
        <div className="body-container">
          {isCorporate ? (
            <CorporateTypography variant="body1">
              {textConstants.CORPORATE_INFO_NEW_PACKAGES_CTA_BODY_TEXT}
            </CorporateTypography>
          ) : (
            <Typography>
              {textConstants.INFO_NEW_PACKAGES_CTA_BODY_TEXT}
            </Typography>
          )}
          <FontAwesomeIcon
            className="mobile-right-chevron"
            icon={faChevronRight}
          />
        </div>
      </ButtonWrap>
    );
  };

  useEffect(() => {
    if (originCode && destinationCode) {
      fetchDepartureCalendar(
        origin as ITripTerminus,
        destination as ITripTerminus
      );
    } else {
      setCalendar();
    }
  }, [originCode, destinationCode, tripCategory]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const previousDeparture = usePrevious(departureDate);
  const previousReturn = usePrevious(returnDate);

  useEffect(() => {
    if (!(departureDate && returnDate) && previousDeparture && previousReturn) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_RESET,
        funnel: TravelAppInterfaceFunnelType.FLIGHTS,
      });
    }
  }, [departureDate, returnDate]);

  useEffect(() => {
    if (departureDate) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.FLIGHTS,
        data: {
          startDate: dayjs(departureDate).format("YYYY-MM-DD"),
        },
      });
      if (tripCategory !== TripCategory.ROUND_TRIP) {
        travelAppInterface.sendEvent({
          name: TravelAppInterfaceEventName.DATES_FINALIZED,
          funnel: TravelAppInterfaceFunnelType.FLIGHTS,
        });
      }
    }
  }, [departureDate, tripCategory]);

  useEffect(() => {
    if (returnDate) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATE_PICKED,
        funnel: TravelAppInterfaceFunnelType.FLIGHTS,
        data: {
          endDate: dayjs(returnDate).format("YYYY-MM-DD"),
        },
      });
    }
  }, [returnDate]);

  useEffect(() => {
    if (departureDate && returnDate) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.DATES_FINALIZED,
        funnel: TravelAppInterfaceFunnelType.FLIGHTS,
      });
    }
  }, [departureDate, returnDate]);

  const resetAutocompleteErrors = () => {
    hasOriginAutocompleteError && setOriginAutocompleteError(false);
    hasDestinationAutocompleteError && setDestinationAutocompleteError(false);
  };

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  // Show loading state while initializing to prevent flickering between steps
  if (isTravelApp && isInitializing) {
    return (
      <Box className="mobile-flight-search-v3">
        <LoadingPopup
          indicatorSize="small"
          indicator={B2BSpinner}
          open={true}
          message="Loading..."
        />
      </Box>
    );
  }

  return (
    <Box
      className={clsx("mobile-flight-search-v3", {
        "date-range-picker":
          currentStep === MobileFlightSearchStep.CalendarPicker,
      })}
    >
      {!isTravelApp && (
        <Header
          className="homepage"
          center={
            <HeaderCenterSection
              originCode={originCode}
              destinationCode={destinationCode}
              currentStep={currentStep}
            />
          }
          left={
            <ActionLink
              className={clsx("mobile-flight-search-header-go-back", {
                hidden: currentStep === MobileFlightSearchStep.LocationSearch,
              })}
              onClick={handleGoBack}
              content={<FontAwesomeIcon icon={faChevronLeft} />}
            />
          }
          right={
            <ActionLink
              className="mobile-flight-search-header-close"
              onClick={() => {
                setCurrentStep(MobileFlightSearchStep.LocationSearch);
                history.push(PATH_HOME);
                resetAutocompleteErrors();
              }}
              content={<CloseButtonIcon />}
              label="Close"
            />
          }
          isMobile
          fullWidth
        />
      )}
      {currentStep === MobileFlightSearchStep.LocationSearch && (
        <Box className="location-search-container">
          <FlightCategoryRadio
            setTripCategory={setTripCategory}
            selectedCategory={tripCategory}
            isMultiCityFlightsAvailable={isMultiCityEnabled}
          />

          <LocationSearch
            onComplete={() => {
              if (tripCategory === TripCategory.MULTI_CITY) {
                populateFlightShopQueryParams({
                  history,
                  useHistoryPush: true,
                  forceQueryUpdate: false,
                  newQueryParams: {
                    multicityFlightShopProgress:
                      MulticityFlightShopStep.ChooseDeparture0,
                  },
                });
              } else {
                setCurrentStep(MobileFlightSearchStep.CalendarPicker);
              }
            }}
            currentTripCategory={tripCategory}
            history={history}
            recentSearches={recentSearches}
            onRecentSearchClick={onRecentSearchClick}
            onSelectLocation={onSelectLocation}
            isAirCXV3Experiment={isAirCXV3Experiment}
          />
          {isPackagesExperiment && renderPackagesCTA()}
        </Box>
      )}
      {currentStep === MobileFlightSearchStep.CalendarPicker && (
        <MobileCalendarPicker
          focusedMonthIndex={focusedMonthIndex}
          returnDate={returnDate}
          departure={departureDate}
          onSelectDates={onSelectDates}
        />
      )}
      <LoadingPopup
        indicatorSize="small"
        indicator={B2BSpinner}
        open={departureDateBucketsCallState === CallState.InProcess}
        message={textConstants.UPDATE_CALENDAR_TEXT}
      />
    </Box>
  );
};

interface IHeaderCenterSectionProps {
  originCode?: string;
  destinationCode?: string;
  currentStep: MobileFlightSearchStep;
}

const HeaderCenterSection = (props: IHeaderCenterSectionProps) => {
  const { originCode, destinationCode, currentStep } = props;
  const headerText =
    currentStep === MobileFlightSearchStep.CalendarPicker
      ? textConstants.CHOOSE_DATE
      : textConstants.FLIGHTS_HEADER;
  return (
    <Box className="header-center-section">
      {originCode &&
      destinationCode &&
      currentStep === MobileFlightSearchStep.CalendarPicker ? (
        <Box className="trip-origin-destination-summary">
          <Box className="origin-destination-container">
            <Typography variant="body1">{headerText}</Typography>
          </Box>
        </Box>
      ) : (
        <Box className="mobile-flight-search-location-label">
          <span>{textConstants.FLIGHTS_HEADER}</span>
        </Box>
      )}
    </Box>
  );
};
